import { useSyncExternalStore } from 'react';

/**
 * The practice's Gmail inbox connection (spec 38). AI Email reads this inbox and
 * sends its replies from it; every other agent email is sent by ELVA itself, so
 * nothing else depends on this. One subscribable store so Settings › Email, the
 * Email page, the AI Team roster, the Settings nav and the Dashboard all show the
 * same state — and so Connect / Disconnect / Switch update every surface at once.
 * Staging seeds it from `?gmail=` and `?role=` (see setGmailFromUrl).
 */

export type GmailState = 'none' | 'connected' | 'expired';

export interface GmailAccount {
  address: string;
  kind: 'Google Workspace' | 'Personal Gmail';
  connectedBy: string;
  lastChecked: string;
  /** When Google stopped access — only meaningful in the `expired` state. */
  stoppedAt: string;
}

/** The one mock account for Alamo Heights. */
export const GMAIL_ACCOUNT: GmailAccount = {
  address: 'frontdesk@alamoheightsdental.com',
  kind: 'Google Workspace',
  connectedBy: 'Ava Thompson · 12 Sep 2026',
  lastChecked: '2 minutes ago · 8 emails today',
  stoppedAt: '30 Sep at 4:12 pm',
};

interface Store {
  state: GmailState;
  /** Only a practice admin may connect, switch or disconnect (spec 38 §0). */
  admin: boolean;
}

const state: Store = { state: 'connected', admin: true };
const listeners = new Set<() => void>();
const subscribe = (fn: () => void): (() => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const emit = (): void => { for (const l of listeners) l(); };

const get = (): Store => state;

/** Apply the staging query params once (`?gmail=`, `?role=`). */
export function setGmailFromUrl(gmail: string | null, role: string | null): void {
  let changed = false;
  if (gmail === 'none' || gmail === 'connected' || gmail === 'expired') {
    if (state.state !== gmail) { state.state = gmail; changed = true; }
  }
  if (role !== null) {
    const admin = role !== 'staff';
    if (state.admin !== admin) { state.admin = admin; changed = true; }
  }
  if (changed) emit();
}

export function setGmailState(next: GmailState): void {
  if (state.state === next) return;
  state.state = next;
  emit();
}

export function connectGmail(): void { setGmailState('connected'); }
export function disconnectGmail(): void { setGmailState('none'); }

export function useGmailStore(): Store {
  return useSyncExternalStore(subscribe, get, get);
}
